import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FiHeart, FiCheck, FiLayers, FiCreditCard, FiAlertCircle, FiGift } from 'react-icons/fi';
import api from '../../services/api';
import { useServerConfig } from '../../hooks/useServerConfig';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import styles from './DonationForm.module.css';


function DonationFormContent({ 
  onClientSecretChange, 
  clientSecretFromParent, 
  preservedOneTimeAmount, 
  setPreservedOneTimeAmount, 
  preservedDonationType, 
  setPreservedDonationType,
  preservedMonthlyAmount: preservedMonthlyAmountFromParent,
  setPreservedMonthlyAmount: setPreservedMonthlyAmountFromParent,
  preservedNumberOfPayments: preservedNumberOfPaymentsFromParent,
  setPreservedNumberOfPayments: setPreservedNumberOfPaymentsFromParent,
  preservedIsForever: preservedIsForeverFromParent,
  setPreservedIsForever: setPreservedIsForeverFromParent,
  preservedSubscriptionData: preservedSubscriptionDataFromParent,
  setPreservedSubscriptionData: setPreservedSubscriptionDataFromParent
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // These hooks will return null if Elements is not rendered yet (which is fine)
  const stripe = useStripe();
  const elements = useElements();

  // Use preserved donation type if available (to prevent reset on remount), otherwise default to 'one-time'
  const [donationType, setDonationType] = useState(preservedDonationType || 'one-time'); // 'one-time' or 'monthly'
  
  // Update donationType when preservedDonationType changes (from parent)
  useEffect(() => {
    if (preservedDonationType && preservedDonationType !== donationType) {
      setDonationType(preservedDonationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preservedDonationType]);
  const [oneTimeAmount, setOneTimeAmount] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [numberOfPayments, setNumberOfPayments] = useState('');
  const [isForever, setIsForever] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [donationFor, setDonationFor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Best Practice: Store paymentIntentId to allow reusing PaymentIntent if checkout is interrupted
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  // Store subscription/subscription schedule data for monthly donations
  // Use preserved subscription data from parent if available (survives remounts)
  const [subscriptionData, setSubscriptionData] = useState(preservedSubscriptionDataFromParent || null);
  
  // Sync subscriptionData when preserved value changes
  useEffect(() => {
    if (preservedSubscriptionDataFromParent && !subscriptionData) {
      setSubscriptionData(preservedSubscriptionDataFromParent);
    }
  }, [preservedSubscriptionDataFromParent, subscriptionData]);
  
  
  // Use preserved values from parent (they survive remounts)
  // These values are stored in the parent component and persist across Elements remounts
  const preservedMonthlyAmount = preservedMonthlyAmountFromParent;
  const preservedNumberOfPayments = preservedNumberOfPaymentsFromParent;
  const preservedIsForever = preservedIsForeverFromParent;
  
  // Helper functions to set preserved values in parent
  // These update the parent component's state, which survives remounts
  const setPreservedMonthlyAmount = useCallback((value) => {
    setPreservedMonthlyAmountFromParent(value);
  }, [setPreservedMonthlyAmountFromParent]);
  
  const setPreservedNumberOfPayments = useCallback((value) => {
    setPreservedNumberOfPaymentsFromParent(value);
  }, [setPreservedNumberOfPaymentsFromParent]);
  
  const setPreservedIsForever = useCallback((value) => {
    setPreservedIsForeverFromParent(value);
  }, [setPreservedIsForeverFromParent]);
  

  // CRITICAL: Sync form state with preserved values when they become available
  // This ensures inputs display values even if component remounts
  useEffect(() => {
    if (preservedMonthlyAmount && (!monthlyAmount || monthlyAmount === '')) {
      setMonthlyAmount(preservedMonthlyAmount.toString());
    }
  }, [preservedMonthlyAmount, monthlyAmount]);

  useEffect(() => {
    if (preservedNumberOfPayments && preservedNumberOfPayments !== 'forever' && (!numberOfPayments || numberOfPayments === '')) {
      setNumberOfPayments(preservedNumberOfPayments.toString());
    }
  }, [preservedNumberOfPayments, numberOfPayments]);

  useEffect(() => {
    if (preservedIsForever && !isForever) {
      setIsForever(true);
    }
  }, [preservedIsForever, isForever]);

  useEffect(() => {
    // Calculate total when amounts change
  }, [oneTimeAmount, monthlyAmount, numberOfPayments]);

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateTotal = () => {
    if (donationType === 'one-time') {
      const amount = preservedOneTimeAmount || parseFloat(oneTimeAmount) || 0;
      return amount.toFixed(2);
    } else {
      // Monthly donation - show total over all payments
      // Use preserved values if available (they persist even if form values are cleared)
      const amount = preservedMonthlyAmount || parseFloat(monthlyAmount) || 0;
      const forever = preservedIsForever || isForever;
      
      if (forever) {
        // For forever subscriptions, we can't calculate a total
        return null;
      }
      
      const payments = preservedNumberOfPayments || numberOfPayments;
      if (amount && payments) {
        const numPayments = typeof payments === 'number' ? payments : parseInt(payments);
        return (amount * (numPayments || 0)).toFixed(2);
      }
      return amount.toFixed(2);
    }
  };

  const [clientSecret, setClientSecret] = useState(clientSecretFromParent || null);
  const [showPaymentElement, setShowPaymentElement] = useState(!!clientSecretFromParent);
  const [showPaymentForm, setShowPaymentForm] = useState(!!clientSecretFromParent);
  const [hasExpressCheckout, setHasExpressCheckout] = useState(false);
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);
  const paymentIntentCreatedRef = useRef(!!clientSecretFromParent);

  // Update local state when parent provides clientSecret
  useEffect(() => {
    if (clientSecretFromParent) {
      // For one-time donations, always show payment element
      if (donationType === 'one-time') {
        setClientSecret(clientSecretFromParent);
        setShowPaymentElement(true);
      } 
      // For monthly donations (including forever), show payment element if we have clientSecret
      else if (donationType === 'monthly') {
        setClientSecret(clientSecretFromParent);
        setShowPaymentElement(true);
      }
    } else if (donationType !== 'one-time' && donationType !== 'monthly' && (clientSecret || clientSecretFromParent)) {
      // Clear if we have a clientSecret but switched away from one-time or monthly
      setClientSecret(null);
      setShowPaymentElement(false);
      if (onClientSecretChange) {
        onClientSecretChange(null);
      }
    }
  }, [clientSecretFromParent, donationType, clientSecret, onClientSecretChange]);

  // Also show payment element when we have clientSecret from parent for one-time donations
  useEffect(() => {
    if (clientSecretFromParent && donationType === 'one-time' && !showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [clientSecretFromParent, donationType, showPaymentElement]);

  // REMOVED: Auto-create payment intent - now only created when user clicks "Proceed to Payment"

  // REMOVED: Auto-create subscription/payment intent - now only created when user clicks "Proceed to Payment"

  // Notify parent when clientSecret changes
  useEffect(() => {
    if (onClientSecretChange && clientSecret) {
      onClientSecretChange(clientSecret);
    }
  }, [clientSecret, onClientSecretChange]);

  // Show payment form when clientSecret becomes available (after "Proceed to Payment" click)
  useEffect(() => {
    if (clientSecret && paymentIntentCreatedRef.current && !showPaymentForm) {
      logger.debug('Client secret became available, showing payment form');
      setShowPaymentForm(true);
      setShowPaymentElement(true);
    }
  }, [clientSecret, showPaymentForm]);

  // Handler for "Proceed to Payment" button
  const handleProceedToPayment = async () => {
    if (creatingPaymentIntent || paymentIntentCreatedRef.current) {
      return; // Already creating or created
    }
    
    setCreatingPaymentIntent(true);
    setError('');
    
    try {
      logger.info('User clicked Proceed to Payment - creating donation payment intent');
      
      if (donationType === 'one-time') {
        // Create one-time donation payment intent
        const currentAmount = oneTimeAmount ? parseFloat(oneTimeAmount) : (preservedOneTimeAmount || 0);

        if (!currentAmount || currentAmount < 0.01 || isNaN(currentAmount)) {
          setError('Please enter a valid donation amount.');
          setCreatingPaymentIntent(false);
          return;
        }

        // Format the displayed value to xx.xx
        setOneTimeAmount(currentAmount.toFixed(2));
        
        const requestBody = {
          oneTimeAmount: currentAmount,
          donationType: 'one-time',
        };
        
        if (firstName && firstName.trim()) {
          requestBody.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          requestBody.lastName = lastName.trim();
        }
        if (email && email.trim()) {
          requestBody.email = email.trim();
        }
        if (phone && phone.trim()) {
          requestBody.phone = phone.trim();
        }
        if (!user && donationFor && donationFor.trim()) {
          requestBody.donationFor = donationFor.trim();
        }
        
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(requestBody)
        });

        if (response.success && response.oneTimePayment) {
          setPreservedOneTimeAmount(currentAmount);
          const newClientSecret = response.oneTimePayment.clientSecret;
          
          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }
          
          setClientSecret(newClientSecret);
          setPaymentIntentId(response.oneTimePayment.paymentIntentId);
          setPreservedDonationType('one-time');
          paymentIntentCreatedRef.current = true;
          
          // Wait a moment for clientSecret to be set
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (newClientSecret) {
            setShowPaymentForm(true);
            setShowPaymentElement(true);
            logger.info('One-time donation payment intent created, showing payment form');
          } else {
            setError('Failed to initialize payment. Please try again.');
            logger.error('Payment intent not created after Proceed to Payment click');
          }
        } else {
          setError(response.message || response.error || 'Failed to create payment intent. Please try again.');
          logger.error('Failed to create one-time donation payment intent', { response });
        }
      } else if (donationType === 'monthly') {
        // Create monthly donation payment intent/subscription
        const currentMonthlyAmount = preservedMonthlyAmount || parseFloat(monthlyAmount);
        const currentIsForever = preservedIsForever || isForever;
        const currentNumberOfPayments = preservedNumberOfPayments || numberOfPayments;

        if (!currentMonthlyAmount || currentMonthlyAmount < 0.01 || isNaN(currentMonthlyAmount)) {
          setError('Please enter a valid monthly donation amount.');
          setCreatingPaymentIntent(false);
          return;
        }

        // Format the displayed value to xx.xx
        setMonthlyAmount(currentMonthlyAmount.toFixed(2));
        
        if (!currentIsForever && (!currentNumberOfPayments || currentNumberOfPayments < 1 || currentNumberOfPayments > 120)) {
          setError('Please select a valid number of payments (1-120) or select Unlimited.');
          setCreatingPaymentIntent(false);
          return;
        }
        
        const requestBody = {
          monthlyAmount: currentMonthlyAmount,
          numberOfPayments: currentIsForever ? 'forever' : currentNumberOfPayments,
          donationType: 'monthly',
        };
        
        if (firstName && firstName.trim()) {
          requestBody.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          requestBody.lastName = lastName.trim();
        }
        if (email && email.trim()) {
          requestBody.email = email.trim();
        }
        if (phone && phone.trim()) {
          requestBody.phone = phone.trim();
        }
        if (!user && donationFor && donationFor.trim()) {
          requestBody.donationFor = donationFor.trim();
        }
        
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(requestBody)
        });

        const responseClientSecret = response?.subscription?.clientSecret || response?.subscriptionSchedule?.clientSecret || response?.clientSecret;
        const responseSubscriptionData = response?.subscription || response?.subscriptionSchedule;

        if (response.success && responseClientSecret) {
          setPreservedMonthlyAmount(currentMonthlyAmount);
          setPreservedIsForever(currentIsForever);
          setPreservedNumberOfPayments(currentIsForever ? 'forever' : currentNumberOfPayments);
          const newClientSecret = responseClientSecret;

          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }

          setClientSecret(newClientSecret);
          setSubscriptionData(responseSubscriptionData || { clientSecret: newClientSecret });
          setPreservedSubscriptionDataFromParent(responseSubscriptionData || { clientSecret: newClientSecret });
          setPreservedDonationType('monthly');
          paymentIntentCreatedRef.current = true;
          
          // Wait a moment for clientSecret to be set
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (newClientSecret) {
            setShowPaymentForm(true);
            setShowPaymentElement(true);
            logger.info('Monthly donation payment intent created, showing payment form');
          } else {
            setError('Failed to initialize payment. Please try again.');
            logger.error('Payment intent not created after Proceed to Payment click');
          }
        } else {
          setError(response.message || response.error || 'Failed to create payment intent. Please try again.');
          logger.error('Failed to create monthly donation payment intent', { response });
        }
      }
    } catch (error) {
      logger.error('Error creating donation payment intent on Proceed to Payment', { error });
      setError('Failed to initialize payment. Please try again.');
    } finally {
      setCreatingPaymentIntent(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError('');
    setSuccess('');

    // Validate inputs based on donation type
    if (donationType === 'one-time') {
      const amountToCheck = preservedOneTimeAmount || parseFloat(oneTimeAmount);
      if (!amountToCheck || amountToCheck <= 0) {
        setError('Please enter a valid one-time donation amount.');
        return;
      }
    } else {
      // Monthly donation - ALWAYS use preserved values if payment element is already shown
      // Otherwise use form values, but also preserve them immediately
      const monthlyAmt = (showPaymentElement && preservedMonthlyAmount) 
        ? preservedMonthlyAmount 
        : (preservedMonthlyAmount || parseFloat(monthlyAmount));
      const forever = (showPaymentElement && preservedIsForever !== null)
        ? preservedIsForever
        : (preservedIsForever || isForever);
      const payments = (showPaymentElement && preservedNumberOfPayments)
        ? preservedNumberOfPayments
        : (preservedNumberOfPayments || numberOfPayments);
      
      // If we have form values but not preserved values, preserve them NOW
      if (!preservedMonthlyAmount && monthlyAmount) {
        setPreservedMonthlyAmount(parseFloat(monthlyAmount));
      }
      if (!preservedNumberOfPayments && numberOfPayments && !isForever) {
        setPreservedNumberOfPayments(parseInt(numberOfPayments));
      }
      if (preservedIsForever === false && isForever) {
        setPreservedIsForever(true);
      }
      
      // Monthly donation - amount is required
      if (!monthlyAmt || monthlyAmt <= 0 || isNaN(monthlyAmt)) {
        setError('Monthly donation amount is required. Please enter a valid amount.');
        return;
      }
      // Number of payments or unlimited option is required
      if (!forever && (!payments || payments === 'forever' || (typeof payments === 'string' && payments.trim() === '') || (typeof payments === 'number' ? payments < 1 : parseInt(payments) < 1) || (typeof payments === 'number' ? payments > 120 : parseInt(payments) > 120))) {
        setError('Number of monthly payments is required. Please enter a number between 1 and 120, or select "Unlimited".');
        return;
      }
    }

    if (!user && (!firstName || !lastName)) {
      setError('Please fill in all required fields (First Name, Last Name).');
      return;
    }

    if (!stripe) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    // If we have a one-time payment and no clientSecret yet, create payment intent first
    // Check both oneTimeAmount and preservedOneTimeAmount
    const amountToUse = preservedOneTimeAmount || parseFloat(oneTimeAmount);
    if (donationType === 'one-time' && amountToUse && !clientSecret && !clientSecretFromParent) {
      setLoading(true);
      try {
        const amount = amountToUse;
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify({
            oneTimeAmount: amount,
            firstName,
            lastName,
            email,
            ...(phone && phone.trim() && { phone: phone.trim() }),
            // Best Practice: Include existing PaymentIntent ID to reuse if checkout was interrupted
            ...(paymentIntentId && { existingPaymentIntentId: paymentIntentId }),
          })
        });

        if (response.success && response.oneTimePayment) {
          setPreservedOneTimeAmount(amount); // Preserve the amount
          // Best Practice: Store clientSecret securely - never log it or expose in URLs
          const newClientSecret = response.oneTimePayment.clientSecret;
          
          // CRITICAL: Update parent FIRST via callback, then update local state
          // This ensures Elements remounts with the new clientSecret before we show PaymentElement
          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }
          
          setClientSecret(newClientSecret);
          
          // Best Practice: Store paymentIntentId to allow reusing PaymentIntent if checkout is interrupted
          setPaymentIntentId(response.oneTimePayment.paymentIntentId);
          // Preserve donation type to prevent reset on Elements remount
          setPreservedDonationType('one-time');
          setShowPaymentElement(true);
          setLoading(false);
          return; // Wait for user to complete payment
        } else {
          setError(response.message || 'Failed to create payment intent. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to create payment. Please try again.');
        setLoading(false);
        return;
      }
    }

    // If we have clientSecret and payment element, confirm the payment
    if (clientSecret && stripe && elements) {
      setLoading(true);
      try {
        // First, submit the elements to validate the form
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || 'Please check your payment information.');
          setLoading(false);
          return;
        }

        // Check if this is a one-time donation or subscription
        // Use preserved subscription data if local state was lost on remount
        // Also check preserved donation type to determine payment type
        const activeSubscriptionData = subscriptionData || preservedSubscriptionDataFromParent;
        const isSubscriptionPayment = !!activeSubscriptionData || preservedDonationType === 'monthly';
        
        if (isSubscriptionPayment) {
          // For subscriptions, ALWAYS use preserved values - they should be set when subscription was created
          // CRITICAL: Use preserved values from parent (they survive remounts)
          const monthlyAmt = preservedMonthlyAmount || parseFloat(monthlyAmount);
          const numPayments = preservedNumberOfPayments || numberOfPayments;
          const forever = preservedIsForever || isForever;
          
          // Validate monthly donation values before confirming payment
          if (!monthlyAmt || monthlyAmt <= 0 || isNaN(monthlyAmt)) {
            setError('Please enter a valid monthly donation amount.');
            setLoading(false);
            return;
          }
          
          if (!forever && (!numPayments || numPayments === 'forever' || (typeof numPayments === 'string' && numPayments.trim() === '') || (typeof numPayments === 'number' ? numPayments < 1 : parseInt(numPayments) < 1))) {
            setError('Please enter a valid number of monthly payments or select "Unlimited".');
            setLoading(false);
            return;
          }
          
          // For subscriptions, confirm the payment intent attached to the subscription invoice
          const { error: confirmError } = await stripe.confirmPayment({
            elements,
            clientSecret: clientSecret,
            confirmParams: {
              return_url: `${window.location.origin}/portal/donation/success`,
            },
            redirect: 'if_required',
          });

          if (confirmError) {
            setError(confirmError.message || 'Payment failed. Please try again.');
            setLoading(false);
            return;
          }

          // Retrieve payment intent with expanded charges to get receipt URL
          const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret, {
            expand: ['charges']
          });

          // Get payment intent ID from the retrieved payment intent (more reliable than parsing client secret)
          const paymentIntentId = paymentIntent?.id;
          
          if (!paymentIntentId) {
            setError('Could not retrieve payment intent ID. Please contact support.');
            setLoading(false);
            return;
          }

          // Get receipt URL from payment intent
          const receiptUrl = paymentIntent?.charges?.data?.[0]?.receipt_url || null;

          // Prepare donation data based on subscription type
          // Use preserved values (already validated above)
          // CRITICAL: Use preserved subscription data which survives remounts
          const activeSubscriptionData = subscriptionData || preservedSubscriptionDataFromParent;
          
          const donationData = {
            donationType: 'monthly',
            monthlyAmount: monthlyAmt,
            isForever: forever,
            user: {
              firstName: firstName,
              lastName: lastName,
              email: email,
              first_name: firstName,
              last_name: lastName
            },
            receiptUrl: receiptUrl,
            paymentIntentId: paymentIntentId
          };

          // Use preserved subscription data to get subscription IDs
          if (forever && activeSubscriptionData?.subscriptionId) {
            donationData.numberOfPayments = 'forever';
            donationData.totalAmount = null;
            donationData.subscriptionId = activeSubscriptionData.subscriptionId;
          } else if (activeSubscriptionData?.subscriptionScheduleId) {
            const paymentsNum = typeof numPayments === 'number' ? numPayments : parseInt(numPayments);
            donationData.numberOfPayments = paymentsNum;
            donationData.totalAmount = monthlyAmt * paymentsNum;
            donationData.subscriptionScheduleId = activeSubscriptionData.subscriptionScheduleId;
            donationData.subscriptionId = activeSubscriptionData.subscriptionId;
          } else if (activeSubscriptionData?.subscriptionId) {
            // Fallback: if we have subscriptionId but no schedule, it's a forever subscription
            const paymentsNum = typeof numPayments === 'number' ? numPayments : parseInt(numPayments);
            donationData.numberOfPayments = forever ? 'forever' : paymentsNum;
            donationData.totalAmount = forever ? null : (monthlyAmt * paymentsNum);
            donationData.subscriptionId = activeSubscriptionData.subscriptionId;
          }

          // Navigate to confirmation page (no /portal prefix needed - basename is set in Router)
          navigate('/donation/success', {
            state: { donationData }
          });
        } else {
          // One-time donation flow
          // Validate that we still have the amount (use preserved amount if available)
          const amountToUse = preservedOneTimeAmount || parseFloat(oneTimeAmount);
          if (!amountToUse || amountToUse <= 0) {
            setError('Please enter a valid donation amount.');
            setLoading(false);
            return;
          }

          // Confirm the payment
          // Include receipt_email in confirmParams to ensure email is captured
          const confirmParams = {
            return_url: `${window.location.origin}/portal/donation/success`,
          };
          
          // Add receipt_email if email is available (helps Stripe capture it)
          if (email && email.trim()) {
            confirmParams.receipt_email = email.trim();
          }
          
          const { error: confirmError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret: clientSecret,
            confirmParams: confirmParams,
            redirect: 'if_required',
          });

          if (confirmError) {
            setError(confirmError.message || 'Payment failed. Please try again.');
            setLoading(false);
            return;
          }

          // Get payment intent ID from the confirmation response (most reliable)
          // If not available, retrieve it separately
          let paymentIntent = confirmedPaymentIntent;
          let paymentIntentId = confirmedPaymentIntent?.id;
          
          if (!paymentIntentId) {
            // Fallback: Retrieve payment intent with expanded charges to get receipt URL
            logger.debug('Payment intent not in confirmPayment response, retrieving separately...');
            try {
              const result = await stripe.retrievePaymentIntent(clientSecret, {
                expand: ['charges']
              });
              paymentIntent = result.paymentIntent;
              paymentIntentId = paymentIntent?.id;
            } catch (retrieveError) {
              logger.error('Error retrieving payment intent', { error: retrieveError });
              // Try to extract from client secret as last resort
              const fallbackId = clientSecret.split('_secret_')[0];
              if (fallbackId && fallbackId.startsWith('pi_')) {
                paymentIntentId = fallbackId;
                logger.debug('Using fallback payment intent ID from client secret', { fallbackId });
              } else {
                setError('Could not retrieve payment intent ID. Please contact support.');
                setLoading(false);
                return;
              }
            }
          }
          
          if (!paymentIntentId) {
            setError('Could not retrieve payment intent ID. Please contact support.');
            setLoading(false);
            return;
          }

          logger.debug('Sending payment intent ID to backend', { 
            paymentIntentId, 
            status: paymentIntent?.status, 
            amount: paymentIntent?.amount 
          });

          // Confirm donation on backend (works for both authenticated and guest users)
          // Add a small delay to ensure Stripe has processed the payment intent
          await new Promise(resolve => setTimeout(resolve, 1000));

          try {
            // Use publicRequest for guest donations, request for authenticated users
            if (user) {
              await api.request('/payments/confirm-donation', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  paymentIntentId: paymentIntentId
                })
              });
            } else {
              await api.publicRequest('/payments/confirm-donation', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  paymentIntentId: paymentIntentId
                })
              });
            }
            logger.debug('Backend confirmation successful');
          } catch (confirmErr) {
            // Continue anyway as payment was successful
            logger.warn('Backend confirmation failed, but payment was successful', { error: confirmErr });
            logger.warn('Error details', {
              message: confirmErr.message,
              response: confirmErr.response
            });
          }

          // Get receipt URL from payment intent
          const receiptUrl = paymentIntent?.charges?.data?.[0]?.receipt_url || null;
          
          // Navigate to confirmation page with donation data (use correct path without /portal prefix due to basename)
          navigate('/donation/success', {
            state: {
              donationData: {
                donationType: 'one-time',
                oneTimeAmount: amountToUse,
                totalAmount: amountToUse,
                user: {
                  firstName: firstName,
                  lastName: lastName,
                  email: email,
                  first_name: firstName,
                  last_name: lastName
                },
                receiptUrl: receiptUrl,
                paymentIntentId: paymentIntentId
              }
            }
          });
        }
      } catch (err) {
          setError(err.message || 'An error occurred during payment confirmation.');
          setLoading(false);
      }
    } else if (donationType === 'monthly') {
      // Monthly donation - use PaymentMethod approach for subscriptions
      // Use preserved values if available (in case form was already submitted once)
      const monthlyAmt = preservedMonthlyAmount || parseFloat(monthlyAmount);
      const forever = preservedIsForever || isForever;
      const payments = preservedNumberOfPayments || numberOfPayments;
      
      // Validate before making API call
      if (!monthlyAmt || monthlyAmt <= 0) {
        setError('Monthly donation amount is required. Please enter a valid amount.');
        setLoading(false);
        return;
      }
      
      if (!forever && (!payments || (typeof payments === 'string' && payments.trim() === '') || (typeof payments === 'number' ? payments < 1 : parseInt(payments) < 1))) {
        setError('Number of monthly payments is required. Please enter a positive number, or select "Unlimited".');
        setLoading(false);
        return;
      }
      
      // For fixed-term monthly donations (not forever), use the new PaymentMethod approach
      if (!forever && payments && parseInt(payments) >= 1) {
        // NEW APPROACH: Use PaymentMethod + Subscription Schedule
        if (!elements) {
          setError('Payment form is not ready. Please wait a moment and try again.');
          setLoading(false);
          return;
        }
        
        setLoading(true);
        try {
          // Step 1: Submit elements to validate the form (REQUIRED before createPaymentMethod)
          const { error: submitError } = await elements.submit();
          if (submitError) {
            setError(submitError.message || 'Please check your payment information.');
            setLoading(false);
            return;
          }

          // Step 2: Create PaymentMethod from PaymentElement (after submit)
          const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            elements,
            params: {
              billing_details: {
                email: email,
                name: `${firstName} ${lastName}`.trim()
              }
            }
          });

          if (pmError) {
            setError(pmError.message || 'Failed to collect payment method. Please check your payment information.');
            setLoading(false);
            return;
          }

          if (!paymentMethod) {
            setError('Failed to create payment method. Please try again.');
            setLoading(false);
            return;
          }

          // Generate idempotency key (donationRequestId)
          const donationRequestId = `donation-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
          

          // Step 2: Create subscription schedule using new route
          const scheduleRequestBody = {
            email,
            name: `${firstName} ${lastName}`.trim(),
            amountUsd: monthlyAmt,
            months: parseInt(payments),
            payment_method_id: paymentMethod.id,
            donationRequestId
          };
          
          // Include donationFor for public users (replaces "Faith Insight Donation")
          if (!user && donationFor && donationFor.trim()) {
            scheduleRequestBody.donationFor = donationFor.trim();
          }
          
          const response = await api.publicRequest('/payments/create-donation-schedule', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
            },
            body: JSON.stringify(scheduleRequestBody)
          });

          if (response.success) {
            // Success! Navigate to confirmation page
            const donationData = {
              donationType: 'monthly',
              monthlyAmount: monthlyAmt,
              numberOfPayments: parseInt(payments),
              totalAmount: monthlyAmt * parseInt(payments),
              subscriptionScheduleId: response.scheduleId,
              subscriptionId: response.subscriptionId,
              status: response.status,
              user: {
                firstName,
                lastName,
                email,
                first_name: firstName,
                last_name: lastName
              }
            };

            // Navigate to confirmation page
            navigate('/donation/success', {
              state: { donationData }
            });
          } else {
            setError(response.message || response.error || 'Failed to create donation schedule. Please try again.');
            setLoading(false);
          }
        } catch (err) {
          setError(err.message || 'Failed to create donation. Please try again.');
          setLoading(false);
        }
        return;
      }
      
      // For "forever" subscriptions or payments > 36, use the old PaymentIntent approach
      setLoading(true);
      try {
        // Ensure numberOfPayments is sent as a number (not string) when not forever
        const numberOfPaymentsToSend = forever ? 'forever' : (typeof payments === 'number' ? payments : parseInt(payments, 10));
        
        const foreverRequestBody = {
          monthlyAmount: monthlyAmt,
          numberOfPayments: numberOfPaymentsToSend,
          firstName,
          lastName,
          email,
          ...(phone && phone.trim() && { phone: phone.trim() }),
        };
        
        // Include donationFor for public users (replaces "Faith Insight Donation")
        if (!user && donationFor && donationFor.trim()) {
          foreverRequestBody.donationFor = donationFor.trim();
        }
        
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(foreverRequestBody)
        });

        if (response.success) {
          // Check if we have a clientSecret for payment collection (subscription/subscription schedule)
          const clientSecret = response.subscription?.clientSecret || response.subscriptionSchedule?.clientSecret;
          
          if (clientSecret) {
            // IMPORTANT: Preserve monthly donation values BEFORE showing payment element
            // This ensures the values are available when the form re-renders
            // Use the values we already calculated (may include preserved values)
            const preservedAmount = monthlyAmt;
            const preservedPayments = forever ? 'forever' : (typeof payments === 'number' ? payments : parseInt(payments));
            
            // Set preserved values first - CRITICAL: These must be set before showing payment element
            setPreservedMonthlyAmount(preservedAmount);
            setPreservedNumberOfPayments(preservedPayments);
            setPreservedIsForever(forever);
            setPreservedOneTimeAmount(null); // Clear one-time amount
            // Preserve donation type to prevent reset on Elements remount
            setPreservedDonationType('monthly');
            
            // Also ensure form state values are set (they should already be set, but ensure they're not empty)
            if (!monthlyAmount || monthlyAmount === '' || parseFloat(monthlyAmount) !== preservedAmount) {
              setMonthlyAmount(preservedAmount.toString());
            }
            if (!numberOfPayments || numberOfPayments === '' || (numberOfPayments !== preservedPayments.toString() && numberOfPayments !== preservedPayments)) {
              if (!forever && preservedPayments !== 'forever') {
                setNumberOfPayments(preservedPayments.toString());
              }
            }
            
            // Now store subscription data in BOTH local and parent state (survives remounts)
            const subscriptionDataToStore = response.subscription || response.subscriptionSchedule;
            setSubscriptionData(subscriptionDataToStore);
            setPreservedSubscriptionDataFromParent(subscriptionDataToStore);
            
            setClientSecret(clientSecret);
            setShowPaymentElement(true);
            
            setLoading(false);
            return; // Wait for user to complete payment
          } else {
            // No client secret - subscription might have been created without payment (shouldn't happen)
            setError('Failed to create payment method collection. Please try again.');
            setLoading(false);
            return;
          }
        } else {
          setError(response.message || 'Failed to create donation. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'An error occurred. Please try again.');
        setLoading(false);
      }
    }
  };

  const totalAmount = calculateTotal();

  return (
    <div className={styles.donationCard}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <FiHeart /> Make a Donation
        </h1>
        <p className={styles.subtitle}>Support our mission with a one-time or recurring donation</p>
      </div>

      {error && <div className={styles.errorMessage}><FiAlertCircle className={styles.errorIcon} />{error}</div>}
      {success && (
        <div className={styles.successMessage}>
          <FiCheck /> {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formSection}>
          <h4 className={styles.sectionTitle}>
            <FiLayers /> Donation Type
          </h4>

          <div className={styles.radioGroup}>
            <label
              className={`${styles.radioOption} ${donationType === 'one-time' ? styles.selected : ''}`}
              onClick={() => {
                // Allow switching donation type even when payment element is shown
                setDonationType('one-time');
                setPreservedDonationType('one-time');
                setMonthlyAmount('');
                setNumberOfPayments('');
                setPreservedMonthlyAmount(null);
                setPreservedNumberOfPayments(null);
                setPreservedIsForever(false);
                // Reset payment element when switching types
                setClientSecret(null);
                setShowPaymentElement(false);
                setShowPaymentForm(false);
                setSubscriptionData(null);
                setPaymentIntentId(null);
                paymentIntentCreatedRef.current = false;
                setError('');
                // Clear parent's clientSecret if callback exists
                if (onClientSecretChange) {
                  onClientSecretChange(null);
                }
              }}
            >
              <input
                type="radio"
                name="donationType"
                value="one-time"
                checked={donationType === 'one-time'}
                onChange={() => {
                  setDonationType('one-time');
                  setPreservedDonationType('one-time');
                  setMonthlyAmount('');
                  setNumberOfPayments('');
                  setPreservedMonthlyAmount(null);
                  setPreservedNumberOfPayments(null);
                  setPreservedIsForever(false);
                  setClientSecret(null);
                  setShowPaymentElement(false);
                  setShowPaymentForm(false);
                  setSubscriptionData(null);
                  setPaymentIntentId(null);
                  paymentIntentCreatedRef.current = false;
                  setError('');
                  if (onClientSecretChange) {
                    onClientSecretChange(null);
                  }
                }}
              />
              <span className={styles.radioLabel}>
                One-Time Donation
              </span>
            </label>

            <label
              className={`${styles.radioOption} ${donationType === 'monthly' ? styles.selected : ''}`}
              onClick={() => {
                setDonationType('monthly');
                setPreservedDonationType('monthly');
                setOneTimeAmount('');
                setPreservedOneTimeAmount(null);
                setIsForever(false);
                setNumberOfPayments('');
                setClientSecret(null);
                setShowPaymentElement(false);
                setShowPaymentForm(false);
                setSubscriptionData(null);
                setPaymentIntentId(null);
                paymentIntentCreatedRef.current = false;
                setError('');
              }}
            >
              <input
                type="radio"
                name="donationType"
                value="monthly"
                checked={donationType === 'monthly'}
                onChange={() => {
                  setDonationType('monthly');
                  setPreservedDonationType('monthly');
                  setOneTimeAmount('');
                  setPreservedOneTimeAmount(null);
                  setIsForever(false);
                  setNumberOfPayments('');
                  setClientSecret(null);
                  setShowPaymentElement(false);
                  setShowPaymentForm(false);
                  setSubscriptionData(null);
                  setPaymentIntentId(null);
                  paymentIntentCreatedRef.current = false;
                  setError('');
                }}
              />
              <span className={styles.radioLabel}>
                Monthly Recurring Donation
              </span>
            </label>
          </div>
        </div>

        <div className={styles.formSection}>
          <h4 className={styles.sectionTitle}>
            <FiGift /> Donation Amount
          </h4>

          {donationType === 'one-time' ? (
            <div>
              <input
                className={styles.input}
                type="text"
                inputMode="decimal"
                id="oneTimeAmount"
                placeholder="$0.00"
                onBlur={(e) => {
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  const val = parseFloat(raw);
                  if (!isNaN(val) && val > 0) {
                    const formatted = val.toFixed(2);
                    setOneTimeAmount(formatted);
                    if (preservedOneTimeAmount) {
                      setPreservedOneTimeAmount(parseFloat(formatted));
                    }
                  }
                }}
                value={oneTimeAmount ? `$${oneTimeAmount}` : (preservedOneTimeAmount ? `$${parseFloat(preservedOneTimeAmount).toFixed(2)}` : '')}
                onChange={(e) => {
                  // Strip $ and non-numeric chars (keep one decimal)
                  let val = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = val.split('.');
                  if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                  if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].substring(0, 2);
                  setOneTimeAmount(val);

                  // Clear preserved amount when user manually changes the input
                  if (preservedOneTimeAmount) {
                    const newAmount = parseFloat(val);
                    const preservedAmount = parseFloat(preservedOneTimeAmount);
                    if (isNaN(newAmount) || Math.abs(newAmount - preservedAmount) > 0.01) {
                      setPreservedOneTimeAmount(null);
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }
                }}
                required
              />
              <div className={styles.quickShortcuts}>
                <button
                  className={styles.shortcutButton}
                  type="button"
                  onClick={() => {
                    const newAmount = 25;
                    const newAmountStr = '25.00';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $25
                </button>
                <button
                  className={styles.shortcutButton}
                  type="button"
                  onClick={() => {
                    const newAmount = 50;
                    const newAmountStr = '50.00';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $50
                </button>
                <button
                  className={styles.shortcutButton}
                  type="button"
                  onClick={() => {
                    const newAmount = 100;
                    const newAmountStr = '100.00';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $100
                </button>
                <button
                  className={styles.shortcutButton}
                  type="button"
                  onClick={() => {
                    const newAmount = 200;
                    const newAmountStr = '200.00';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $200
                </button>
                <button
                  className={styles.shortcutButton}
                  type="button"
                  onClick={() => {
                    const newAmount = 500;
                    const newAmountStr = '500.00';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $500
                </button>
                <button
                  className={styles.shortcutButton}
                  type="button"
                  onClick={() => {
                    if (!(showPaymentElement && !!clientSecret)) {
                      setOneTimeAmount('1000.00');
                      setPreservedOneTimeAmount(1000);
                    }
                  }}
                  disabled={showPaymentForm && !!clientSecret}
                >
                  $1000
                </button>
              </div>
            </div>
                ) : (
                  <>
                    <div>
                      <input
                        className={styles.input}
                        type="text"
                        inputMode="decimal"
                        id="monthlyAmount"
                        placeholder="$0.00"
                        value={
                          monthlyAmount
                            ? `$${monthlyAmount}`
                            : (preservedMonthlyAmount ? `$${parseFloat(preservedMonthlyAmount).toFixed(2)}` : '')
                        }
                        onChange={(e) => {
                          if (!(showPaymentForm && !!clientSecret)) {
                            let val = e.target.value.replace(/[^0-9.]/g, '');
                            const parts = val.split('.');
                            if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                            if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].substring(0, 2);
                            setMonthlyAmount(val);
                            if (clientSecret) {
                              setClientSecret(null);
                              setShowPaymentElement(false);
                              setShowPaymentForm(false);
                              setPaymentIntentId(null);
                              paymentIntentCreatedRef.current = false;
                              if (onClientSecretChange) {
                                onClientSecretChange(null);
                              }
                            }
                          }
                        }}
                        onBlur={(e) => {
                          if (!(showPaymentForm && !!clientSecret)) {
                            const raw = e.target.value.replace(/[^0-9.]/g, '');
                            const val = parseFloat(raw);
                            if (!isNaN(val) && val > 0) {
                              const formatted = val.toFixed(2);
                              setMonthlyAmount(formatted);
                              if (preservedMonthlyAmount) {
                                setPreservedMonthlyAmount(val);
                              }
                            }
                          }
                        }}
                        readOnly={showPaymentForm && !!clientSecret}
                        required
                      />
                      <div className={styles.quickShortcuts}>
                        <button
                  className={styles.shortcutButton}
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('25.00');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $25
                        </button>
                        <button
                  className={styles.shortcutButton}
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('50.00');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $50
                        </button>
                        <button
                  className={styles.shortcutButton}
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('100.00');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $100
                        </button>
                        <button
                  className={styles.shortcutButton}
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('200.00');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $200
                        </button>
                        <button
                  className={styles.shortcutButton}
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('500.00');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $500
                        </button>
                        <button
                  className={styles.shortcutButton}
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('1000.00');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $1000
                        </button>
                      </div>
              </div>
                    <div>
                      <input
                className={styles.input}
                        type="number"
                        id="numberOfPayments"
                        min="1"
                        max="120"
                        placeholder={(preservedIsForever || isForever) ? "Unlimited" : "Enter number of payments"}
                        value={
                          (preservedIsForever || isForever)
                            ? '' 
                            : (preservedNumberOfPayments 
                                ? (typeof preservedNumberOfPayments === 'number' 
                                    ? preservedNumberOfPayments.toString() 
                                    : preservedNumberOfPayments === 'forever' 
                                      ? '' 
                                      : preservedNumberOfPayments.toString())
                                : numberOfPayments)
                        }
                        onChange={(e) => {
                          if (!(showPaymentForm && !!clientSecret)) {
                            setNumberOfPayments(e.target.value);
                            setIsForever(false);
                            // Clear payment intent when number of payments changes
                            if (clientSecret) {
                              setClientSecret(null);
                              setShowPaymentElement(false);
                              setShowPaymentForm(false);
                              setPaymentIntentId(null);
                              paymentIntentCreatedRef.current = false;
                              if (onClientSecretChange) {
                                onClientSecretChange(null);
                              }
                            }
                          }
                        }}
                        readOnly={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                        required={!(preservedIsForever || isForever)}
                      />
                <div className={`${styles.quickShortcuts} ${styles.durationShortcuts}`}>
                  <button
                    className={`${styles.shortcutButton} ${(preservedNumberOfPayments === 6 || numberOfPayments === '6') && !(preservedIsForever || isForever) ? styles.selected : ''}`}
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        setNumberOfPayments('6');
                        setIsForever(false);
                        // Clear payment intent when number of payments changes
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                  >
                    6 mo
                  </button>
                  <button
                    className={`${styles.shortcutButton} ${(preservedNumberOfPayments === 12 || numberOfPayments === '12') && !(preservedIsForever || isForever) ? styles.selected : ''}`}
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        setNumberOfPayments('12');
                        setIsForever(false);
                        // Clear payment intent when number of payments changes
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                  >
                    12 mo
                  </button>
                  <button
                    className={`${styles.shortcutButton} ${(preservedNumberOfPayments === 24 || numberOfPayments === '24') && !(preservedIsForever || isForever) ? styles.selected : ''}`}
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        setNumberOfPayments('24');
                        setIsForever(false);
                        // Clear payment intent when number of payments changes
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                  >
                    24 mo
                  </button>
                  <button
                    className={`${styles.shortcutButton} ${(preservedIsForever || isForever) ? styles.selected : ''}`}
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        if (preservedIsForever || isForever) {
                          setIsForever(false);
                          setNumberOfPayments('');
                        } else {
                          setIsForever(true);
                          setNumberOfPayments('forever');
                        }
                        // Clear payment intent when switching between unlimited and limited
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={showPaymentForm && !!clientSecret}
                  >
                    Unlimited
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {!user && (
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Your Information</h4>

            <div>
              <label htmlFor="donationFor" className={styles.label}>Donation For: *</label>
              <input
                className={styles.input}
                type="text"
                id="donationFor"
                required
                placeholder="e.g., General Donation/Sadaqah"
                value={donationFor}
                onChange={(e) => setDonationFor(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="firstName" className={styles.label}>First Name *</label>
              <input
                className={styles.input}
                type="text"
                id="firstName"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="lastName" className={styles.label}>Last Name *</label>
              <input
                className={styles.input}
                type="text"
                id="lastName"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="email" className={styles.label}>Email (Optional)</label>
              <input
                className={styles.input}
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="phone" className={styles.label}>Phone (Optional)</label>
              <input
                className={styles.input}
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Show PaymentElement for:
            1. Old flow: when we have clientSecret (one-time or forever subscriptions)
            2. New flow: when monthly donation is selected with valid amount/payments for PaymentMethod creation
        */}
        {showPaymentForm && (() => {
          // For one-time donations: show if we have clientSecret (from parent or local) and showPaymentElement is true
          // For monthly donations: 
          //   - Forever subscriptions: show if we have clientSecret (created by subscription)
          //   - Limited subscriptions: show if we have valid amount/payments for PaymentMethod creation
          const hasClientSecret = !!(clientSecret || clientSecretFromParent);
          const shouldShowOneTime = showPaymentElement && hasClientSecret && donationType === 'one-time';
          
          // For forever subscriptions, show payment element if we have a clientSecret
          const shouldShowMonthlyForever = donationType === 'monthly' && (isForever || preservedIsForever) && 
            hasClientSecret && showPaymentElement;
          
          // For limited monthly subscriptions, show if we have valid amount/payments
          const shouldShowMonthlyLimited = donationType === 'monthly' && !isForever && !preservedIsForever && 
            (monthlyAmount || preservedMonthlyAmount) && 
            (numberOfPayments || preservedNumberOfPayments) &&
            parseInt(numberOfPayments || preservedNumberOfPayments) >= 1;
          
          const shouldShow = shouldShowOneTime || shouldShowMonthlyForever || shouldShowMonthlyLimited;
          
          return shouldShow;
        })() && (
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>
              <FiCreditCard /> Payment Information
            </h4>

            <div className={styles.paymentElementContainer}>
              {clientSecret && (
                <div className={styles.expressCheckoutWrapper}>
                  <ExpressCheckoutElement
                    options={{
                      buttonType: {
                        applePay: 'donate',
                        googlePay: 'donate',
                        paypal: 'pay'
                      },
                      buttonHeight: 48,
                      buttonTheme: {
                        applePay: 'black',
                        googlePay: 'black',
                        paypal: 'gold'
                      },
                      layout: {
                        overflow: 'never'
                      },
                      paymentMethods: {
                        applePay: 'auto',
                        googlePay: 'auto',
                        paypal: 'auto',
                        link: 'auto'
                      }
                    }}
                    onConfirm={async () => {
                      try {
                        setError('');
                        const { error: confirmError } = await stripe.confirmPayment({
                          elements,
                          clientSecret,
                          confirmParams: {
                            return_url: window.location.origin + '/portal/donation-confirmation'
                          },
                          redirect: 'if_required'
                        });
                        if (confirmError) {
                          setError(confirmError.message || 'Payment failed. Please try again.');
                        } else {
                          navigate('/donation-confirmation', {
                            state: {
                              donationData: {
                                donationType,
                                oneTimeAmount: preservedOneTimeAmount || oneTimeAmount,
                                monthlyAmount: preservedMonthlyAmount || monthlyAmount,
                                numberOfPayments: preservedIsForever || isForever ? 'forever' : (preservedNumberOfPayments || numberOfPayments),
                                isForever: preservedIsForever || isForever,
                                user: user || { firstName, lastName, email },
                                paymentIntentId: clientSecret.split('_secret_')[0]
                              }
                            }
                          });
                        }
                      } catch (err) {
                        logger.error('Express checkout confirm error', { error: err });
                        setError(err.message || 'Payment failed. Please try again.');
                      }
                    }}
                    onReady={(event) => {
                      const available = event?.availablePaymentMethods || {};
                      setHasExpressCheckout(Boolean(available.applePay || available.googlePay || available.paypal || available.link));
                    }}
                    onError={(event) => {
                      logger.error('Express checkout error', { error: event?.error?.message || event });
                      setError(event?.error?.message || 'Wallet payment failed. Please try another method.');
                    }}
                  />
                  {hasExpressCheckout && (
                    <div className={styles.orPayDivider}>
                      <span>or pay with card</span>
                    </div>
                  )}
                </div>
              )}

              <PaymentElement
                options={{
                  wallets: {
                    googlePay: 'never',
                    applePay: 'never'
                  },
                  layout: {
                    type: 'accordion',
                    defaultCollapsed: false
                  }
                }}
              />
            </div>
          </div>
        )}

        {((donationType === 'one-time' && (oneTimeAmount || preservedOneTimeAmount)) || (donationType === 'monthly' && (monthlyAmount || preservedMonthlyAmount) && ((numberOfPayments || preservedNumberOfPayments) || isForever || preservedIsForever))) && (
          <div className={styles.summaryBox}>
            {donationType === 'one-time' ? (
              <div className={`${styles.summaryRow} ${styles.summaryTotalNoBorder}`}>
                <span className={styles.summaryLabel}>Total Amount:</span>
                <span className={styles.summaryValue}>{formatCurrency(preservedOneTimeAmount || totalAmount)}</span>
              </div>
            ) : (
              <>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Monthly Amount:</span>
                  <span className={styles.summaryValue}>{formatCurrency(preservedMonthlyAmount || monthlyAmount)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Duration:</span>
                  <span className={styles.summaryValue}>{(preservedIsForever || isForever) ? 'Unlimited' : `${preservedNumberOfPayments || numberOfPayments} months`}</span>
                </div>
                {!(preservedIsForever || isForever) && (
                  <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                    <span className={styles.summaryLabel}>Total Amount:</span>
                    <span className={styles.summaryValue}>{formatCurrency(
                      preservedMonthlyAmount && preservedNumberOfPayments 
                        ? preservedMonthlyAmount * (typeof preservedNumberOfPayments === 'number' ? preservedNumberOfPayments : parseInt(preservedNumberOfPayments))
                        : totalAmount
                    )}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!showPaymentForm ? (
          <button
            className={styles.submitButton}
            type="button"
            onClick={handleProceedToPayment}
            disabled={creatingPaymentIntent || loading || 
              (donationType === 'one-time' && (!oneTimeAmount || parseFloat(oneTimeAmount) < 0.01)) ||
              (donationType === 'monthly' && (!monthlyAmount || parseFloat(monthlyAmount) < 0.01 || (!isForever && (!numberOfPayments || numberOfPayments < 1 || numberOfPayments > 120))))}
            style={{ width: '100%' }}
          >
            {creatingPaymentIntent ? (
              <>Creating secure payment...</>
            ) : (
              <>
                <FiCreditCard style={{ marginRight: '0.5rem' }} />
                Proceed to Payment
              </>
            )}
          </button>
        ) : (
          <button
            className={styles.submitButton} type="submit" disabled={loading || !stripe || (showPaymentElement && !elements)}>
            {loading ? (
              'Processing...'
            ) : (
              <>
                <FiCreditCard size={16} />
                {(() => {
                  const isMonthlyForever = donationType === 'monthly' && (preservedIsForever || isForever);
                  const amount = donationType === 'monthly'
                    ? (preservedMonthlyAmount || parseFloat(monthlyAmount) || 0)
                    : (preservedOneTimeAmount || parseFloat(oneTimeAmount) || totalAmount || 0);
                  if (isMonthlyForever && amount) {
                    return `Donate ${formatCurrency(amount)}/month`;
                  }
                  if (donationType === 'monthly' && amount) {
                    return `Donate ${formatCurrency(amount)}/month`;
                  }
                  if (amount) {
                    return `Donate ${formatCurrency(amount)}`;
                  }
                  return 'Donate';
                })()}
              </>
            )}
          </button>
        )}
      </form>
    </div>
  );
}

function DonationForm() {
  const { config, loading, error } = useServerConfig();
  const [clientSecret, setClientSecret] = useState(null);
  const [preservedOneTimeAmount, setPreservedOneTimeAmount] = useState(null); // Preserve amount after payment intent creation
  const [preservedDonationType, setPreservedDonationType] = useState(null); // Preserve donation type to prevent reset on remount
  // Preserve monthly donation values in parent to survive remounts
  const [preservedMonthlyAmount, setPreservedMonthlyAmount] = useState(null);
  const [preservedNumberOfPayments, setPreservedNumberOfPayments] = useState(null);
  const [preservedIsForever, setPreservedIsForever] = useState(false);
  // Preserve subscription data to identify subscription payments after remount
  const [preservedSubscriptionData, setPreservedSubscriptionData] = useState(null);
  const [shouldLoadStripe, setShouldLoadStripe] = useState(false);

  const handleClientSecretChange = useCallback((newClientSecret) => {
    setClientSecret(newClientSecret);
    setShouldLoadStripe(!!newClientSecret);
  }, []);
  
  
  // Use useMemo to ensure Stripe promise is only created once
  // IMPORTANT: Only depend on the stripe key value, not the entire config object
  // This prevents Elements from complaining about stripe prop changes
  const stripeKey = useMemo(() => {
    // Try server config first
    let key = config?.stripePublishableKey;
    
    // Fallback to REACT_APP_STRIPE_PUBLISHABLE_KEY if server config doesn't have it
    if (!key && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    }
    
    return key;
  }, [config?.stripePublishableKey]);
  
  const stripePromise = useMemo(() => {
    if (stripeKey && shouldLoadStripe) {
      return loadStripe(stripeKey);
    }
    return null;
  }, [stripeKey, shouldLoadStripe]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.donationCard}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading payment configuration...
          </div>
        </div>
      </div>
    );
  }

  if (error || !config || !config.stripePublishableKey) {
    return (
      <div className={styles.container}>
        <div className={styles.donationCard}>
          <div className={styles.errorMessage}>
            Stripe is not properly configured. Please check your server configuration.
            {error && <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Error: {error}</div>}
          </div>
        </div>
      </div>
    );
  }

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#B89569',
      colorText: '#171717',
      colorTextSecondary: '#6B7280',
      colorTextPlaceholder: '#9CA3AF',
      colorBackground: '#ffffff',
      colorDanger: '#DC2626',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      borderRadius: '8px',
      fontSizeBase: '13px',
      spacingUnit: '4px'
    },
    rules: {
      '.Input': {
        borderRadius: '8px',
        border: '0.5px solid #D4B48A',
        color: '#171717',
        padding: '0.875rem 1rem',
        fontSize: '13px'
      },
      '.Input:focus': {
        borderColor: '#B89569',
        boxShadow: '0 0 0 2px #F5EFE6'
      },
      '.Tab': {
        borderRadius: '8px',
        border: '0.5px solid #D4B48A',
        color: '#171717'
      },
      '.Tab:hover': {
        borderColor: '#B89569',
        backgroundColor: '#F5EFE6'
      },
      '.Tab--selected': {
        borderColor: '#B89569',
        backgroundColor: '#F5EFE6',
        color: '#7A5E3A'
      },
      '.Tab:focus': {
        boxShadow: '0 0 0 2px #F5EFE6'
      },
      '.Block': {
        borderRadius: '8px',
        border: '0.5px solid #D4B48A'
      },
      '.Label': {
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
        color: '#171717',
        fontSize: '13px'
      },
      '.TabIcon': { color: '#7A5E3A' },
      '.TabLabel': { color: '#171717' },
      '.Text': { color: '#171717' },
      '.Link': { color: '#B89569' }
    }
  };

  // Determine Elements options - must have either clientSecret or mode
  // For one-time donations, we need to wait for clientSecret before initializing Elements
  // This prevents the "setup mode cannot be confirmed with Payment Intent" error
  
  const elementsOptions = { clientSecret, appearance, paymentMethodCreation: 'manual' };
  
  // Force remount Elements when clientSecret changes from null to a value or vice versa
  const elementsKey = clientSecret ? `donation-${clientSecret.substring(0, 20)}` : 'donation-form-setup';
  
  return (
    <div className={styles.container}>
      {stripePromise ? (
        <Elements
          stripe={stripePromise}
          options={elementsOptions}
          key={elementsKey}
        >
          <DonationFormContent
            onClientSecretChange={handleClientSecretChange}
            clientSecretFromParent={clientSecret}
            preservedOneTimeAmount={preservedOneTimeAmount}
            setPreservedOneTimeAmount={setPreservedOneTimeAmount}
            preservedDonationType={preservedDonationType}
            setPreservedDonationType={setPreservedDonationType}
            preservedMonthlyAmount={preservedMonthlyAmount}
            setPreservedMonthlyAmount={setPreservedMonthlyAmount}
            preservedNumberOfPayments={preservedNumberOfPayments}
            setPreservedNumberOfPayments={setPreservedNumberOfPayments}
            preservedIsForever={preservedIsForever}
            setPreservedIsForever={setPreservedIsForever}
            preservedSubscriptionData={preservedSubscriptionData}
            setPreservedSubscriptionData={setPreservedSubscriptionData}
          />
        </Elements>
      ) : (
        <Elements stripe={null} options={{ mode: 'setup', currency: 'usd', appearance }} key="donation-form-deferred">
          <DonationFormContent
            onClientSecretChange={handleClientSecretChange}
            clientSecretFromParent={clientSecret}
            preservedOneTimeAmount={preservedOneTimeAmount}
            setPreservedOneTimeAmount={setPreservedOneTimeAmount}
            preservedDonationType={preservedDonationType}
            setPreservedDonationType={setPreservedDonationType}
            preservedMonthlyAmount={preservedMonthlyAmount}
            setPreservedMonthlyAmount={setPreservedMonthlyAmount}
            preservedNumberOfPayments={preservedNumberOfPayments}
            setPreservedNumberOfPayments={setPreservedNumberOfPayments}
            preservedIsForever={preservedIsForever}
            setPreservedIsForever={setPreservedIsForever}
            preservedSubscriptionData={preservedSubscriptionData}
            setPreservedSubscriptionData={setPreservedSubscriptionData}
          />
        </Elements>
      )}
    </div>
  );
}

export default DonationForm;
